// @flow
import { List } from 'immutable';
import moment from 'moment';
import { noNaN } from 'utils/utils';

// import { getLogger } from 'utils/logger';

import * as types from './types';

// const log = getLogger('data/accounts/transformers');

export const transformAccountToRequestData = (account: types.AccountRecordTypes) => {
  switch (account.type) {
    case 'BANK':
      return transformBankAccountToRequestData(((account: any): types.BankAccount));
    case 'CASH':
      return transformCashAccountToRequestData(((account: any): types.BankAccount));
    case 'CREDIT':
      return transformCreditAccountToRequestData(((account: any): types.CreditAccount));
    case 'INVESTMENT':
      return transformInvestmentAccountToRequestData(((account: any): types.InvestmentAccount));
    case 'LOAN':
      return transformLoanAccountToRequestData(((account: any): types.LoanAccount));
    case 'OTHER_ASSET':
      return transformOtherAssetAccountToRequestData(((account: any): types.LoanAccount));
    case 'OTHER_LIABILITY':
      return transformOtherLiabilityAccountToRequestData(((account: any): types.LoanAccount));
    case 'REAL_ESTATE':
      return transformRealEstateAccountToRequestData(((account: any): types.RealEstateAccount));
    case 'VEHICLE':
      return transformVehicleAccountToRequestData(((account: any): types.VehicleAccount));
    case 'UNKNOWN':
    default:
      return transformUnknownAccountToRequestData(((account: any): types.UnknownAccount));
  }
};

export function transformResponseToAccounts(response: any) {
  return response.data.resources.map((account) => {
    switch (account.type) {
      case 'CASH':
        return transformApiDataToCashAccount(account);
      case 'BANK':
        return transformApiDataToBankAccount(account);
      case 'CREDIT':
        return transformApiDataToCreditAccount(account);
      case 'INVESTMENT':
        return transformApiDataToInvestmentAccount(account);
      case 'LOAN':
        return transformApiDataToLoanAccount(account);
      case 'OTHER_ASSET':
        return transformApiDataToOtherAssetAccount(account);
      case 'OTHER_LIABILITY':
        return transformApiDataToOtherLiabilityAccount(account);
      case 'REAL_ESTATE':
        return transformApiDataToRealEstateAccount(account);
      case 'VEHICLE':
        return transformApiDataToVehicleAccount(account);
      case 'GOAL':
        return transformApiDataToGoalAccount(account);
      case 'UNKNOWN':
      default:
        return transformApiDataToUnknownAccount(account);
    }
  });
}

export function transformResponseToInvestmentAccounts(response: any) {
  return response.data.resources.map((account) => transformQ17ApiDataToInvestmentAccount(account));
}

export function transformInvestmentAccountToQ17RequestData(account: types.AccountRecordTypes) {
  return transformInvestmentAccountToQ17ApiRequestData(account);
}

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Account API Responses to Accounts
// /////////////////////////////////////////////////////////////////////////////

function transformApiDataToAggregatorMap(data) {
  return ({
    id: data.id,
    cpId: data.cpId,
    channel: data.channel,

    subType: data.subType,

    accountName: data.accountName,
    accountNumberMasked: data.accountNumberMasked,

    aggStatus: data.aggStatus,
    aggStatusCode: data.aggStatusCode,
    aggStatusDetail: data.aggStatusDetail,

    lastRefreshAttemptedAt: data.lastRefreshAttemptedAt,
    lastRefreshSuccessfulAt: data.lastRefreshSuccessfulAt,
  });
}

function transformApiDataToBankAccount(data) {
  return types.mkBankAccount({
    ...transformApiDataToAccountMap(data),
    interestRate: data.interestRate,
    maturityDateOn: data.maturityDateOn,
    minimumNoFeeBalance: data.minimumNoFeeBalance,
    monthlyFee: data.monthlyFee,
    freeBillPay: data.freeBillPay,
    atmFeeReimbursement: data.atmFeeReimbursement,
    aggregators: !(data.cpData && data.cpData.aggregators) ? null : List(
      data.cpData.aggregators.map((aggregator) => (types.mkAccountAggregator({
        ...transformApiDataToAggregatorMap(aggregator),
      })))
    ),
  });
}

function transformApiDataToCashAccount(data) {
  return types.mkCashAccount({
    ...transformApiDataToAccountMap(data),
  });
}

function transformApiDataToCreditAccount(data) {
  return types.mkCreditAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators) ? null : List(
      data.cpData.aggregators.map((aggregator) => (types.mkAccountAggregator({
        ...transformApiDataToAggregatorMap(aggregator),
      })))
    ),
  });
}

function transformApiDataToGoalAccount(data) {
  return types.mkGoalAccount({
    ...transformApiDataToAccountMap(data),
  });
}

function transformApiDataToInvestmentAccount(data) {
  return types.mkInvestmentAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators) ? null : List(
      data.cpData.aggregators.map((aggregator) => (types.mkAccountAggregator({
        ...transformApiDataToAggregatorMap(aggregator),
      })))
    ),
  });
}

function transformApiDataToLoanAccount(data) {
  return types.mkLoanAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators) ? null : List(
      data.cpData.aggregators.map((aggregator) => (types.mkAccountAggregator({
        ...transformApiDataToAggregatorMap(aggregator),
      })))
    ),
  });
}

function transformApiDataToOtherAssetAccount(data) {
  return types.mkOtherAssetAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators) ? null : List(
      data.cpData.aggregators.map((aggregator) => (types.mkAccountAggregator({
        ...transformApiDataToAggregatorMap(aggregator),
      })))
    ),
  });
}

function transformApiDataToOtherLiabilityAccount(data) {
  return types.mkOtherLiabilityAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators) ? null : List(
      data.cpData.aggregators.map((aggregator) => (types.mkAccountAggregator({
        ...transformApiDataToAggregatorMap(aggregator),
      })))
    ),
  });
}

function transformApiDataToRealEstateAccount(data) {
  return types.mkRealEstateAccount({
    ...transformApiDataToAccountMap(data),
    line1: data.address ? data.address.line1 : null,
    line2: data.address ? data.address.line2 : null,
    city: data.address ? data.address.city : null,
    state: data.address ? data.address.state : null,
    zipCode: data.address ? data.address.zipCode : null,
  });
}

function transformApiDataToVehicleAccount(data) {
  return types.mkVehicleAccount({
    ...transformApiDataToAccountMap(data),
    year: data.year,
    make: data.make,
    model: data.model,
    trim: data.trim,
    mileage: data.mileage,
  });
}

function transformApiDataToUnknownAccount(data) {
  return types.mkUnknownAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators) ? null : List(
      data.cpData.aggregators.map((aggregator) => (types.mkAccountAggregator({
        ...transformApiDataToAggregatorMap(aggregator),
      })))
    ),
  });
}

function transformQ17ApiDataToInvestmentAccount(data) {
  // todo: remove when investment accounts are migrated in QCS
  //   what about INVESTMENT_TAXABLE, INVESTMENT_TAXABLE_DEFERRED, ESOP, ESPP, TRUST, TDA,
  //
  let subType = null;
  switch (data.investmentAccountType) {
    case 'INV_ACCT_TYPE_BROKERAGE':
      subType = 'BROKERAGE';
      break;
    case 'INV_ACCT_TYPE_401K':
      subType = '401K';
      break;
    case 'INV_ACCT_TYPE_IRA':
      subType = 'IRA';
      break;
    case 'INV_ACCT_TYPE_529':
      subType = '529';
      break;
    case 'INV_ACCT_TYPE_UGMA':
      subType = 'UGMA';
      break;
    case 'INV_ACCT_TYPE_403B':
      subType = '403B';
      break;
    case 'INV_ACCT_TYPE_KEOGH':
      subType = 'KEOGH';
      break;
    case 'INV_ACCT_TYPE_SEP':
      subType = 'SEP';
      break;
    case 'INV_ACCT_TYPE_SIMPLE':
      subType = 'SIMPLE_IRA';
      break;
    case 'INV_ACCT_TYPE_OTHER':
      subType = 'OTHER_INVESTMENT';
      break;
    default:
      subType = null;
      break;
  }

  return types.mkInvestmentAccount({
    ...transformApiDataToAccountMap(data),
    type: 'INVESTMENT',
    subType,
    name: data.userAccountName,
    isDeleted: data.deleted,
    cashBalance: data.cashBalance,
    currency: 'USD',
  });
}

function transformApiDataToAccountMap(data) {
  return {
    id: data.id,
    institutionLoginId: data.institutionLoginId,

    name: data.name ? data.name : 'UNKNOWN',
    subType: data.subType ? data.subType : 'UNKNOWN',
    usageType: data.usageType,

    description: data.description,
    notes: data.notes,

    isExcludedFromAccountBar: Boolean(data.isExcludedFromAccountBar),
    isExcludedFromBudgets: Boolean(data.isExcludedFromBudgets),
    isExcludedFromReports: Boolean(data.isExcludedFromReports),

    onlineBalance: data.onlineBalance,
    onlineBalanceAt: data.onlineBalanceAt,

    recurringTxn: data.recurringTxn && { unitType: data.recurringTxn.unitType, value: noNaN(data.recurringTxn.value) },

    currency: data.currency,

    isClosed: data.isClosed,
    closedOn: data.closedOn,

    balanceAsOf: data.balanceAsOf,
    balanceAsOfOn: data.balanceAsOfOn,

    currentBalanceAsOf: data.currentBalanceAsOf,
    currentBalanceAsOfOn: data.currentBalanceAsOfOn,

    isDeleted: data.isDeleted,
    isIgnored: data.isIgnored,

    modifiedAt: data.modifiedAt ? moment(data.modifiedAt).valueOf() : null,
    createdAt: data.createdAt,
  };
}

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Accounts to Account API Request Data
// /////////////////////////////////////////////////////////////////////////////

function transformBankAccountToRequestData(account: types.BankAccount) {
  return {
    type: 'BANK',
    ...transformBaseAccountToRequestData(account),
    interestRate: account.interestRate,
    maturityDateOn: account.maturityDateOn,
    minimumNoFeeBalance: account.minimumNoFeeBalance,
    monthlyFee: account.monthlyFee,
    freeBillPay: account.freeBillPay,
    atmFeeReimbursement: account.atmFeeReimbursement,
  };
}

function transformCashAccountToRequestData(account: types.BankAccount) {
  return {
    type: 'CASH',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformCreditAccountToRequestData(account: types.CreditAccount) {
  return {
    type: 'CREDIT',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformInvestmentAccountToRequestData(account: types.InvestmentAccount) {
  return {
    type: 'INVESTMENT',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformLoanAccountToRequestData(account: types.LoanAccount) {
  return {
    type: 'LOAN',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformOtherAssetAccountToRequestData(account: types.LoanAccount) {
  return {
    type: 'OTHER_ASSET',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformOtherLiabilityAccountToRequestData(account: types.LoanAccount) {
  return {
    type: 'OTHER_LIABILITY',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformRealEstateAccountToRequestData(account: types.RealEstateAccount) {
  let address = null;
  if (account.line1 || account.line2 || account.city || account.state || account.zipCode) {
    address = {
      line1: account.line1,
      line2: account.line2,
      city: account.city,
      state: account.state,
      zipCode: account.zipCode,
    };
  }
  return {
    type: 'REAL_ESTATE',
    ...transformBaseAccountToRequestData(account),
    address,
  };
}

function transformVehicleAccountToRequestData(account: types.RealEstateAccount) {
  return {
    type: 'VEHICLE',
    ...transformBaseAccountToRequestData(account),
    year: account.year,
    make: account.make,
    model: account.model,
    trim: account.trim,
    mileage: account.mileage,
  };
}

function transformUnknownAccountToRequestData(account: types.UnknownAccount) {
  return {
    ...transformBaseAccountToRequestData(account),
  };
}

function transformBaseAccountToRequestData(account: types.AccountRecordTypes) {
  const id = account.id !== '0' ? account.id : null;

  const requestData: Object = {
    id,
    clientId: account.clientId,
    institutionLoginId: account.institutionLoginId,

    name: account.name,
    subType: account.subType,
    usageType: account.usageType,

    description: account.description,
    notes: account.notes,

    isExcludedFromAccountBar: account.isExcludedFromAccountBar,
    isExcludedFromBudgets: account.isExcludedFromBudgets,
    isExcludedFromReports: account.isExcludedFromReports,

    onlineBalance: account.onlineBalance,
    onlineBalanceAt: account.onlineBalanceAt,

    currency: account.currency,

    isClosed: account.isClosed,
    closedOn: account.closedOn,

    isIgnored: account.isIgnored,

    recurringTxn: account.recurringTxn,

    balanceAsOf: account.balanceAsOf,
    balanceAsOfOn: account.balanceAsOfOn,

    currentBalanceAsOf: account.currentBalanceAsOf,
    currentBalanceAsOfOn: account.currentBalanceAsOfOn,
  };

  const aggregator = account.aggregators && account.aggregators.size > 0 && account.aggregators.get(0);
  if (aggregator) {
    requestData.cpData = {
      aggregators: [{
        channel: aggregator.channel,
        cpId: aggregator.cpId,

        subType: aggregator.subType,

        accountName: aggregator.accountName,
        accountNumberMasked: aggregator.accountNumberMasked,
      }],
    };
  }

  return requestData;
}

function transformInvestmentAccountToQ17ApiRequestData(account: types.AccountRecordTypes) {
  const id = account.id !== '0' ? account.id : null;
  let investmentAccountType = null;

  switch (account.subType) {
    case 'BROKERAGE':
      investmentAccountType = 'INV_ACCT_TYPE_BROKERAGE';
      break;
    case '401K':
      investmentAccountType = 'INV_ACCT_TYPE_401K';
      break;
    case 'IRA':
      investmentAccountType = 'INV_ACCT_TYPE_IRA';
      break;
    case '529':
      investmentAccountType = 'INV_ACCT_TYPE_529';
      break;
    case 'UGMA':
      investmentAccountType = 'INV_ACCT_TYPE_UGMA';
      break;
    case '403B':
      investmentAccountType = 'INV_ACCT_TYPE_403B';
      break;
    case 'KEOGH':
      investmentAccountType = 'INV_ACCT_TYPE_KEOGH';
      break;
    case 'SEP':
      investmentAccountType = 'INV_ACCT_TYPE_SEP';
      break;
    case 'SIMPLE_IRA':
      investmentAccountType = 'INV_ACCT_TYPE_SIMPLE';
      break;
    case 'OTHER_INVESTMENT':
      investmentAccountType = 'INV_ACCT_TYPE_OTHER';
      break;
    default:
      investmentAccountType = null;
      break;
  }

  return {
    id,
    clientId: undefined,
    investmentAccountType,
    accountNumberLast4: undefined,
    userAccountName: account.name,
    fiName: undefined,
    cashBalance: account.cashBalance,
    isDeleted: account.isDeleted,
    isExcludedFromAccountBar: account.isExcludedFromAccountBar,
    isExcludedFromReports: account.isExcludedFromReports,
    isClosed: account.isClosed,
    deleted: account.isDeleted,
  };
}

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Refresh Statusus
// /////////////////////////////////////////////////////////////////////////////

export function transformResponseToRefreshStatus(response: any) {

  return types.makeRefreshResponse({
    jobId: response.jobId,

    isProcessing: response.isProcessing,
    status: response.status,

    institutionLogins: List(
      response.institutionLogins.map((login) => types.makeRefreshInstitutionLogin({
        id: login.id,
        isProcessing: login.isProcessing,
        status: login.status,
        credentialsBlob: login.credentialsBlob,
        submitUrl: login.pollingReference,
        aggregators: !login.aggregators ? null : List(
          login.aggregators.map((aggregator) => types.makeRefreshAggregator({
            channel: aggregator.channel,   // todo: QCS is not returning this, should put in defect/enhancement

            isProcessing: aggregator.isProcessing,
            aggStatus: aggregator.aggStatus,
            cpAggStatus: aggregator.cpAggStatus,
            cpAggStatusDetail: aggregator.cpAggStatusDetail,

            mfaChallenges: aggregator.mfaChallenges,
          }))
        ),
      }))
    ),
  });
}

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Accounts to Account API Request Data (batch)
// /////////////////////////////////////////////////////////////////////////////

export function transformAccountsToRequestData(accounts: List<types.Account>) {
  return {
    resources: accounts.reduce((list, account) => { // eslint-disable-line
      list.push(transformAccountToRequestData(account));
      return list;
    }, []),
  };
}


